import { useDispatch } from "react-redux";
import { useCallback, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";

import { Dispatch } from "../utils/store";
import { ToastStatus } from "@/models/toasts";

const useToasts = () => {
  const dispatch = useDispatch<Dispatch>();

  const create = useCallback(
    (status: ToastStatus, message?: string) => {
      const id = uuidv4();
      dispatch.toasts.create({ id, status, message });
      return id;
    },
    [dispatch]
  );

  const update = useCallback(
    (id: string, status: ToastStatus, message?: string) => {
      dispatch.toasts.update({ id, status, ...(!!message && { message }) });
      return id;
    },
    [dispatch]
  );

  const loading = useCallback(
    (id?: string, message?: string) => {
      if (id) {
        return update(id, "loading", message);
      }
      return create("loading", message);
    },
    [create, update]
  );

  const success = useCallback(
    (id?: string, message?: string) => {
      if (id) {
        return update(id, "success", message);
      }
      return create("success", message);
    },
    [create, update]
  );

  const error = useCallback(
    (id?: string, message?: string) => {
      if (id) {
        return update(id, "error", message);
      }
      return create("error", message);
    },
    [create, update]
  );

  const close = useCallback(
    (id: string) => dispatch.toasts.close({ id }),
    [dispatch]
  );

  const toasts = useMemo(
    () => ({
      update,
      success,
      error,
      loading,
      close,
      create,
    }),
    [update, success, error, close, create, loading]
  );

  return toasts;
};

export default useToasts;
